import { OrderMessageCard } from "../OrderDetails/OrderMessageCard";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { StatusBadge } from "../Badges/StatusBadge";
import { statusScheduled, statusNew } from "@design-system";
import { DetailHeader, Inline, Text } from "@design-system";
import { SimpleList, RouteTimeline } from "@components";
import { useWorkspaceData } from "@data";
import { Driver, OrderTemplate } from "../../models";
import { OrderParticipantCards } from "../OrderDetails/OrderParticipantCards";
import { OrderPriceDetails } from "../OrderDetails/OrderPriceDetails";
import { getTemplatePriceItems } from "../../helpers/orderTemplatePricing";
import { RecurrenceOverview } from "./RecurrenceOverview";

export interface OrderTemplateDetailsProps {
  template?: OrderTemplate;
}

/**
 * Right-hand detail panel for the order-templates split view. Surfaces the full
 * template snapshot: base info, recurrence schedule, route stops and cargo.
 */
export const OrderTemplateDetails: FC<OrderTemplateDetailsProps> = ({
  template,
}) => {
  const { t } = useTranslation(["orders", "common"]);
  const { drivers } = useWorkspaceData();

  const driver = useMemo(
    () => drivers?.find((d: Driver) => d.email === template?.driverId),
    [drivers, template?.driverId],
  );

  if (!template) return null;

  const isRecurring = !!template.recurrence;
  const isActive = template.recurrence?.active === true;

  const priceItems = getTemplatePriceItems(template, {
    transport: t("templates.details.transport_pricing"),
    unnamedProduct: t("common:unnamed_product"),
    piece: t("templates.pricing.piece"),
    timeUnit: t("templates.pricing.time_unit"),
  });

  const infoItems = [
    ...(template.projectNumber && !template.description && !template.comment
      ? [
          {
            title: t("templates.details.project_number"),
            value: template.projectNumber,
          },
        ]
      : []),
  ];

  return (
    <>
      <DetailHeader>
        <Inline gap={3}>
          <Text as="p" variant="detail-title">
            {template.name}
          </Text>
          {isRecurring && (
            <StatusBadge
              status={
                isActive
                  ? t("templates.recurring_badge")
                  : t("templates.paused_badge")
              }
              bgStyle={isActive ? statusScheduled : statusNew}
            />
          )}
        </Inline>
      </DetailHeader>

      <div className="px-4 pb-4 sm:px-6">
        <OrderParticipantCards
          customer={template.customer}
          driver={driver}
          driverFallback={template.driverId || t("templates.no_driver")}
        />
        {template.description && (
          <OrderMessageCard
            title={t("common:description")}
            message={template.description}
            projectNumber={template.projectNumber}
          />
        )}
        {template.comment && (
          <OrderMessageCard
            title={t("common:comment")}
            message={template.comment}
            projectNumber={
              template.description ? undefined : template.projectNumber
            }
          />
        )}
      </div>
      {infoItems.length > 0 && <SimpleList items={infoItems} />}

      {isRecurring && (
        <section className="px-4 py-4 sm:px-6">
          <Text as="h3" variant="section-title" className="mb-3">
            {t("templates.details.recurrence_title")}
          </Text>
          <RecurrenceOverview template={template} />
        </section>
      )}

      {(template.route?.length ?? 0) > 0 && (
        <div className="px-4 py-4 sm:px-6">
          <Text as="h3" variant="section-title" className="mb-2">
            {t("templates.details.route_title")}
          </Text>
          <RouteTimeline
            variant="template"
            stops={(template.route ?? []).map((stop, index) => ({
              ...stop,
              dayOffset: template.stopDayOffsets?.[index] ?? 0,
            }))}
            cargo={template.cargo}
          />
        </div>
      )}

      <section className="px-4 py-4 sm:px-6">
        <Text as="h3" variant="section-title" className="mb-2">
          {t("common:price_details")}
        </Text>
        <p className="mb-3 text-sm text-secondary">
          {t(
            priceItems.length
              ? "templates.pricing.saved_rates_hint"
              : "templates.pricing.no_rates",
          )}
        </p>
        {priceItems.length > 0 && (
          <OrderPriceDetails items={priceItems} ratesOnly />
        )}
      </section>
    </>
  );
};

export default OrderTemplateDetails;
